// ─────────────────────────────────────────────────────────────────
// Glass-i — PVB / Interlayer INVENTORY page (Stage INTERLAYER-STOCK-2).
//
// The SEPARATE interlayer stock view — deliberately NOT the glass Current Stock
// page. Two tabs: live m² Balances and the movement Ledger. Every figure comes
// from the interlayer_current_stock / interlayer_stock_ledger tables that the
// Purchase inward-posting routes interlayer lines into (rolls → m²). Read-only.
// Area shown at 3 dp (rule 5A); rolls are whole/received counts.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Tabs, Input, Select, Tag, Tooltip, Typography, Alert, Button, Space, message,
  Modal, Form, InputNumber, Radio } from 'antd';
import { ReloadOutlined, BlockOutlined, PlusOutlined } from '@ant-design/icons';

import { interlayerStockApi } from './interlayerStockApi';
import { itemsApi } from '../../masters/items/itemsApi';
import useAuth from '../../../../hooks/useAuth';

const CAN_ADJUST = 'erp.inventory.interlayer.adjust';
function hasPerm(me, key) {
  const list = (me && me.permissions) || [];
  return list.includes('*') || list.includes(key);
}

const { Title, Text } = Typography;

const NF3 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const NF0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
function fmtArea(v) { const n = Number(v); return Number.isFinite(n) ? NF3.format(n) : '—'; }
function fmtNum(v) { const n = Number(v); return Number.isFinite(n) ? NF0.format(n) : '—'; }
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v); if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
}
const typeTag = (v) => (v ? <Tag color="geekblue">{String(v).toUpperCase()}</Tag> : <Text type="secondary">—</Text>);
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;
function fmtSigned(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const s = NF3.format(Math.abs(n));
  return n < 0 ? `− ${s}` : (n > 0 ? `+ ${s}` : s);
}

// Manual opening-balance / correction modal. deltaSqm is signed: + adds, − reduces.
function AdjustStockModal({ open, onClose, onDone }) {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('ADD');

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoadingItems(true);
    itemsApi.listInterlayers({ pageSize: 200 })
      .then((res) => { if (alive) setItems(((res && res.rows) || []).map((r) => ({ value: r.itemId, label: r.itemName }))); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoadingItems(false); });
    return () => { alive = false; };
  }, [open]);
  useEffect(() => { if (open) { form.resetFields(); setMode('ADD'); } }, [open, form]);

  const submit = async () => {
    let vals;
    try { vals = await form.validateFields(); } catch { return; }
    const qty = Math.abs(Number(vals.qty));
    const deltaSqm = mode === 'REDUCE' ? -qty : qty;
    setSaving(true);
    try {
      await interlayerStockApi.adjust({ itemId: vals.itemId, deltaSqm, reason: vals.reason });
      message.success(`Stock ${mode === 'REDUCE' ? 'reduced' : 'added'}.`);
      onClose();
      onDone && onDone();
    } catch (e) { message.error(errMsg(e, 'Adjustment failed.')); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={submit} okText="Post adjustment" confirmLoading={saving}
      title="Adjust PVB / interlayer stock" destroyOnClose maskClosable={false} width={480}>
      <Alert type="info" showIcon className="mb-3"
        message="Manual stock adjustment (m²)"
        description="Add opening / found stock, or reduce for a correction / write-off. Recorded as an ADJUSTMENT in the ledger. A reduction can't take a balance below zero." />
      <Form form={form} layout="vertical" initialValues={{ qty: null }}>
        <Form.Item name="itemId" label="Interlayer item" rules={[{ required: true, message: 'Pick an interlayer item.' }]}>
          <Select showSearch optionFilterProp="label" loading={loadingItems} options={items} placeholder="Select interlayer item" />
        </Form.Item>
        <Form.Item label="Action">
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} optionType="button" buttonStyle="solid">
            <Radio.Button value="ADD">Add stock</Radio.Button>
            <Radio.Button value="REDUCE">Reduce</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="qty" label="Quantity (m²)"
          rules={[{ required: true, message: 'Enter a quantity.' }, { type: 'number', min: 0.001, message: 'Must be greater than zero.' }]}>
          <InputNumber min={0} step={0.001} precision={3} style={{ width: '100%' }} placeholder="0.000" />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'A reason is required.' }]}>
          <Input.TextArea rows={2} maxLength={500} placeholder="e.g. Opening balance / stock-count correction" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function BalancesTab({ onDataChange }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const timer = useRef(null);
  const { me } = useAuth();
  const canAdjust = hasPerm(me, CAN_ADJUST);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await interlayerStockApi.stock({ page, pageSize, search: search || undefined });
      setRows((res && res.rows) || []); setTotal((res && res.total) || 0);
    } catch (e) { message.error(errMsg(e, 'Failed to load PVB stock.')); }
    finally { setLoading(false); }
  }, [page, pageSize, search]);
  useEffect(() => { load(); }, [load]);

  const onSearch = useCallback((val) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { setPage(1); setSearch(val); }, 300);
  }, []);

  const columns = useMemo(() => ([
    { title: 'Interlayer item', dataIndex: 'itemName', ellipsis: true,
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: 'Type', dataIndex: 'interlayerType', width: 90, render: typeTag },
    { title: 'Thk (mm)', dataIndex: 'thicknessMm', width: 84, align: 'right',
      render: (v) => (v != null ? <span className="tabular-nums">{v}</span> : <Text type="secondary">—</Text>) },
    { title: 'Balance (m²)', dataIndex: 'sqmtBalance', width: 130, align: 'right',
      render: (v) => <span className="tabular-nums" style={{ fontWeight: 700, color: '#0f4fb0' }}>{fmtArea(v)}</span> },
    { title: 'Rolls recd', dataIndex: 'rollsBalance', width: 100, align: 'right',
      render: (v) => <span className="tabular-nums">{fmtNum(v)}</span> },
    { title: 'UOM', dataIndex: 'uom', width: 70 },
    { title: 'Last movement', dataIndex: 'lastMovementAt', width: 130, render: (v) => fmtDate(v) }
  ]), []);

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <Input.Search allowClear placeholder="Search interlayer item" style={{ width: 260 }} onChange={(e) => onSearch(e.target.value)} />
        <Space>
          {canAdjust ? <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdjustOpen(true)}>Add / adjust stock</Button> : null}
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Space>
      </div>
      <Table rowKey="itemId" size="small" loading={loading} columns={columns} dataSource={rows} sticky scroll={{ x: 'max-content' }}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, size: 'small', pageSizeOptions: ['25', '50', '100'],
          onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
          showTotal: (t) => `${t} interlayer item${t === 1 ? '' : 's'} in stock` }}
        locale={{ emptyText: loading ? ' ' : 'No PVB stock yet — receive interlayer rolls through Purchase (PR → PO → GRN → Bill → Post), or use “Add / adjust stock” to enter an opening balance.' }} />
      <AdjustStockModal open={adjustOpen} onClose={() => setAdjustOpen(false)}
        onDone={() => { load(); if (onDataChange) onDataChange(); }} />
    </>
  );
}

function LedgerTab({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [direction, setDirection] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await interlayerStockApi.ledger({ page, pageSize, direction: direction || undefined });
      setRows((res && res.rows) || []); setTotal((res && res.total) || 0);
    } catch (e) { message.error(errMsg(e, 'Failed to load PVB ledger.')); }
    finally { setLoading(false); }
    // refreshKey re-runs the fetch after a stock adjustment posted on the Balances tab.
  }, [page, pageSize, direction, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => ([
    { title: 'Date', dataIndex: 'movementDate', width: 120, render: (v) => fmtDate(v) },
    { title: 'Interlayer item', dataIndex: 'itemName', ellipsis: true },
    { title: 'Direction', dataIndex: 'direction', width: 100,
      render: (v) => <Tag color={v === 'INWARD' ? 'green' : v === 'OUTWARD' ? 'red' : 'default'}>{v}</Tag> },
    { title: 'Source', key: 'src', width: 150,
      render: (_, r) => (r.sourceNo ? <span>{r.sourceType} · {r.sourceNo}</span> : <Text type="secondary">{r.sourceType || '—'}</Text>) },
    { title: 'Rolls', dataIndex: 'rolls', width: 80, align: 'right', render: (v) => (v != null ? fmtNum(v) : '—') },
    { title: 'Change (m²)', dataIndex: 'movementSqmt', width: 120, align: 'right',
      render: (v) => {
        const n = Number(v);
        const color = Number.isFinite(n) ? (n < 0 ? '#cf1322' : (n > 0 ? '#237804' : undefined)) : undefined;
        return <span className="tabular-nums" style={{ color }}>{fmtSigned(v)}</span>;
      } },
    { title: 'Balance (m²)', dataIndex: 'sqmtBalanceAfter', width: 120, align: 'right',
      render: (v) => <span className="tabular-nums">{v != null ? fmtArea(v) : '—'}</span> }
  ]), []);

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <Select allowClear placeholder="All movements" style={{ width: 160 }} value={direction}
          onChange={(v) => { setDirection(v || undefined); setPage(1); }}
          options={[{ value: 'INWARD', label: 'Inward' }, { value: 'OUTWARD', label: 'Outward' }, { value: 'ADJUSTMENT', label: 'Adjustment' }]} />
        <Button icon={<ReloadOutlined />} onClick={load} />
      </div>
      <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={rows} sticky scroll={{ x: 'max-content' }}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, size: 'small', pageSizeOptions: ['25', '50', '100'],
          onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
          showTotal: (t) => `${t} movement${t === 1 ? '' : 's'}` }}
        locale={{ emptyText: loading ? ' ' : 'No interlayer movements yet.' }} />
    </>
  );
}

export default function PvbInventoryPage() {
  // Bumped when a stock adjustment posts on the Balances tab, so the Ledger tab
  // (kept mounted by AntD Tabs) re-fetches and shows the new movement.
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="p-4">
      <div className="mb-3">
        <Title level={4} style={{ margin: 0 }}><BlockOutlined style={{ marginRight: 8 }} />PVB / Interlayer Inventory</Title>
        <Text type="secondary">Separate interlayer film stock (m²), received in rolls through Purchase. Kept apart from glass inventory.</Text>
      </div>
      <Alert type="info" showIcon className="mb-3"
        message="Separate from glass stock"
        description="Interlayer purchases (GRN/Bill) post here in m², never into the glass Current Stock. Roll→m² uses each item's roll size set on the Interlayer Items master." />
      <Card size="small" bodyStyle={{ paddingTop: 8 }}>
        <Tabs defaultActiveKey="balances" items={[
          { key: 'balances', label: 'Balances', children: <BalancesTab onDataChange={() => setRefreshKey((k) => k + 1)} /> },
          { key: 'ledger', label: 'Ledger', children: <LedgerTab refreshKey={refreshKey} /> }
        ]} />
      </Card>
    </div>
  );
}
